import type { OnInit } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import type { OnyxOptionsGroup } from '@onyx/angular';
import {
  OnyxDatepickerComponent,
  onyxDateValidator,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxModalService,
  OnyxUploadComponent,
} from '@onyx/angular';
import type { CompanyDriverPermission } from '../../../../management-panel/dictionaries/common/interfaces/company-driver-permission';
import type { DictionariesDriverPermissionModalData } from '../../../../management-panel/dictionaries/dictionaries/dictionaries-driver-permissions/dictionaries-driver-permission-modal/dictionaries-driver-permission-modal.component';
import { DictionariesDriverPermissionModalComponent } from '../../../../management-panel/dictionaries/dictionaries/dictionaries-driver-permissions/dictionaries-driver-permission-modal/dictionaries-driver-permission-modal.component';

export type DriverPermissionFormGroup = ReturnType<
  typeof DriverPermissionFormComponent.buildForm
>;

@Component({
  selector: 'app-driver-permission-form',
  imports: [
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxDatepickerComponent,
    OnyxUploadComponent,
    OnyxIconButtonComponent,
  ],
  templateUrl: './driver-permission-form.component.html',
  styleUrl: './driver-permission-form.component.scss',
})
export class DriverPermissionFormComponent implements OnInit {
  private readonly modalService = inject(OnyxModalService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<DriverPermissionFormGroup>();
  public readonly permissions = input.required<
    OnyxOptionsGroup<string, { permission: CompanyDriverPermission }>[] | null
  >();
  public readonly index = input.required<number>();

  public readonly remove = output<void>();

  protected readonly I18N = 'drivers.driverForm.permissions';

  public ngOnInit(): void {
    const controls = this.form().controls;
    const type = toSignal(controls.type.valueChanges, {
      initialValue: controls.type.value,
      injector: this.injector,
    });

    effect(
      () => {
        const permissionUuid = type();
        const permission = this.permissions()
          ?.flatMap((group) => group.options)
          .find(({ value }) => value === permissionUuid)?.permission;
        if (!permission) {
          controls.expirationDate.setValidators([onyxDateValidator]);
          return;
        }

        controls.expirationDate.setValidators([
          ...(permission.requiredExpirationDate ? [Validators.required] : []),
          onyxDateValidator,
        ]);
        controls.expirationDate.updateValueAndValidity();
      },
      { injector: this.injector },
    );
  }

  protected addPermission(name?: string): void {
    this.modalService.open<DictionariesDriverPermissionModalData>(
      DictionariesDriverPermissionModalComponent,
      { mode: OnyxFormMode.ADD, name },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      type: fb.control<string | null>(null, [Validators.required]),
      expirationDate: fb.control<string | null>(null, [onyxDateValidator]),
      scans: fb.control<string[]>([]),
    });
  }
}
